import dynamic from 'next/dynamic';
import { useCallback, useEffect, useReducer, useState } from 'react';

const Card = dynamic(() => import('@components/Card/Card'), { ssr: false });

import { cardContext } from '@context/card-context';
import { cardFieldEditContext } from '@context/card-field-edit-context';
import CardHeaderEditProvider from '@context/card-header-edit/provider';
import useCardFieldEditContext from '@hooks/use-card-field-edit-context';
import { DEFAULT_CARD_COLOR, extractCardFields, extractCardMetadata } from '@modules/card-helpers';
import { cardFieldsReducer } from '@reducers/card-reducers';
import type { CardMetadata, Card as CardType } from 'types';

interface CardViewProps {
  card: CardType | undefined;
  loading?: boolean;
  mode?: 'fit' | 'standalone';
}

const CardView: React.FC<React.PropsWithChildren<CardViewProps>> = ({
  card,
  loading = false,
  mode = 'standalone',
}) => {
  const [cardFieldEditContextProps] = useCardFieldEditContext();
  const [cardMetadata, setCardMetadata] = useState<CardMetadata>();
  const [cardFields, cardFieldsDispatch] = useReducer(cardFieldsReducer, {
    firstName: '',
    linkedAttributes: [],
    fieldOrder: [],
  });

  useEffect(() => {
    if (!card) return;

    const cardMetadata = extractCardMetadata(card);
    const cardFields = extractCardFields(card);

    setCardMetadata(cardMetadata);
    cardFieldsDispatch({ type: 'UPDATE_ALL', value: cardFields });
  }, [card]);

  const handleSaveFieldChanges = useCallback(() => {
    // Do nothing
  }, []);

  function handleRollbackFieldChanges() {
    // Do nothing
  }

  function handleQueueActionOnSave() {
    // Do nothing
  }

  function handleToggleFieldLock() {
    // Do nothing
  }

  function handleToggleFieldTypeLock() {
    // Do nothing
  }

  function handleToggleLayoutImagesLocked() {
    // Do nothing
  }

  function handleToggleLockAllFieldTypes() {
    // Do nothing
  }

  function handleToggleColorLock() {
    // Do nothing
  }

  function handleToggleQRCodeLock() {
    // Do nothing
  }

  return (
    <cardContext.Provider
      value={{
        canLock: false,
        cardColor: cardMetadata?.color || DEFAULT_CARD_COLOR,
        cardFields: cardFields,
        cardId: cardMetadata?.cardId ?? '',
        userId: cardMetadata?.userId ?? '',
        orgId: cardMetadata?.orgId ?? '',
        cardFieldsDispatch: cardFieldsDispatch,
        colorLocked: cardMetadata?.colorLocked ?? false,
        layoutImagesLocked: cardMetadata?.layoutImagesLocked ?? false,
        lockedFields: cardMetadata?.lockedFields ?? [],
        lockedFieldTypes: cardMetadata?.lockedFieldTypes ?? [],
        allFieldTypesLocked: cardMetadata?.allFieldTypesLocked ?? false,
        qrCodeLocked: cardMetadata?.qrCodeLocked ?? false,
        queueActionOnSave: handleQueueActionOnSave,
        rollbackChanges: handleRollbackFieldChanges,
        saveChanges: handleSaveFieldChanges,
        toggleColorLock: handleToggleColorLock,
        toggleFieldLock: handleToggleFieldLock,
        toggleFieldTypeLock: handleToggleFieldTypeLock,
        toggleLayoutImagesLocked: handleToggleLayoutImagesLocked,
        toggleLockAllFieldTypes: handleToggleLockAllFieldTypes,
        toggleQRCodeLock: handleToggleQRCodeLock,
      }}
    >
      <cardFieldEditContext.Provider value={cardFieldEditContextProps}>
        <CardHeaderEditProvider>
          <Card editMode={false} loading={loading} mode={mode} />
        </CardHeaderEditProvider>
      </cardFieldEditContext.Provider>
    </cardContext.Provider>
  );
};

export default CardView;
